import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Drawer, InputNumber, Switch, Tag, Typography } from "antd";
import { Box as Bed, CheckCircle, DollarSign, Globe, Lock, TrendingUp, X } from "react-feather";
import dayjs from "dayjs";
import "../distributionPreview.css";

const { Text } = Typography;

const currencySymbol = (code) => {
  if (code === "INR") return "₹";
  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  if (code === "EUR") return "€";
  return `${code || ""} `;
};

const money = (value, currency) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "—";
  return `${currencySymbol(currency)}${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const minCandidate = (rows) => {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => Number(a.amount) - Number(b.amount))[0];
};

const rateValues = (row) => [
  row?.occ1_rate,
  row?.occ2_rate,
  ...Object.values(row?.guest_wise_rates || {}),
]
  .map(Number)
  .filter((n) => Number.isFinite(n) && n >= 0);

function otaRateRows(room, date) {
  const rows = [];
  (room?.ratePlans || []).forEach((rp) => {
    const raw = rp?.rates?.[date];
    const occupancies = Array.isArray(rp?.occupancies) && rp.occupancies.length
      ? rp.occupancies
      : raw && typeof raw === "object"
        ? Object.keys(raw).map(Number).filter(Boolean)
        : [rp?.occ || rp?.occupancy || 2];

    rows.push({
      id: String(rp.id),
      title: rp.title || "OTA Rate Plan",
      occupancies,
      rates: occupancies.reduce((acc, occ) => {
        const value =
          raw && typeof raw === "object"
            ? raw?.[occ]
            : raw;
        const numeric = Number(value);
        acc[occ] =
          value === null ||
          value === undefined ||
          value === "" ||
          !Number.isFinite(numeric)
            ? null
            : numeric;
        return acc;
      }, {}),
    });
  });
  return rows;
}

function beRows(rateOptions, date, beReadState) {
  const explicitByRate = {};
  (beReadState?.overridesByDate?.[date] || []).forEach((row) => {
    explicitByRate[String(Number(row?.rate_id))] = row;
  });

  return (rateOptions || []).map((r) => {
    const rateId = String(Number(r.rate_id ?? r.id));
    const resolved = date && r?.rates?.[date] ? r.rates[date] : r;
    const masterGuests =
      r?.guest_wise_rates && typeof r.guest_wise_rates === "object"
        ? { ...r.guest_wise_rates }
        : {};
    const effectiveGuests =
      resolved?.guest_wise_rates &&
      typeof resolved.guest_wise_rates === "object"
        ? { ...resolved.guest_wise_rates }
        : masterGuests;

    return {
      id: rateId,
      rateId: Number(rateId),
      title:
        r.display_label ||
        r.rate_name ||
        r.title ||
        r.name ||
        r.meal_plan ||
        "Booking Engine Rate",
      isGuestWise: Boolean(r.is_guest_wise_rate),
      occ1: resolved?.occ1_rate == null ? null : Number(resolved.occ1_rate),
      occ2: resolved?.occ2_rate == null ? null : Number(resolved.occ2_rate),
      guestWise: effectiveGuests,
      masterOcc1: r?.occ1_rate == null ? null : Number(r.occ1_rate),
      masterOcc2: r?.occ2_rate == null ? null : Number(r.occ2_rate),
      masterGuestWise: masterGuests,
      currencyCode: String(
        resolved?.currency_code || r?.currency_code || "INR"
      ).toUpperCase(),
      source: explicitByRate[rateId] ? "date_override" : "master",
    };
  });
}

function getDirectSummary(rateOptions, date, beReadState) {
  const overrideCandidates = [];
  const row = beReadState?.rowsByDate?.[date] || null;

  if (
    row?.override_rate !== null &&
    row?.override_rate !== undefined &&
    row?.override_rate !== ""
  ) {
    const amount = Number(row.override_rate);
    if (Number.isFinite(amount) && amount >= 0) {
      overrideCandidates.push({
        amount,
        source: "Date override",
        title: "Base date rate",
      });
    }
  }

  (beReadState?.overridesByDate?.[date] || []).forEach((override) => {
    const option = (rateOptions || []).find(
      (rate) =>
        Number(rate?.rate_id ?? rate?.id) === Number(override?.rate_id)
    );
    rateValues(override).forEach((amount) => {
      overrideCandidates.push({
        amount,
        source: "Date override",
        title:
          option?.display_label ||
          option?.rate_name ||
          option?.title ||
          option?.name ||
          option?.meal_plan ||
          "Booking Engine Rate",
      });
    });
  });

  if (overrideCandidates.length) return minCandidate(overrideCandidates);

  const master = [];
  (rateOptions || []).forEach((rate) => {
    rateValues(rate).forEach((amount) => {
      master.push({
        amount,
        source: "Master rate",
        title:
          rate?.display_label ||
          rate?.rate_name ||
          rate?.title ||
          rate?.name ||
          rate?.meal_plan ||
          "Booking Engine Rate",
      });
    });
  });
  if (master.length) return minCandidate(master);

  const defaults = beReadState?.roomTypeDefaults || {};
  const roomTypeCandidates = [
    { amount: Number(defaults?.base_rate_single), title: "Single default" },
    { amount: Number(defaults?.base_rate_double), title: "Double default" },
  ].filter((item) => Number.isFinite(item.amount) && item.amount > 0);

  if (!roomTypeCandidates.length) return null;
  return minCandidate(
    roomTypeCandidates.map((item) => ({
      ...item,
      source: "Room Type price",
    }))
  );
}

function getOtaSummary(room, date) {
  const candidates = [];
  (room?.ratePlans || []).forEach((rp) => {
    const raw = rp?.rates?.[date];
    const values =
      raw && typeof raw === "object" ? Object.values(raw) : [raw];
    values.forEach((value) => {
      const amount = Number(value);
      if (Number.isFinite(amount) && amount > 0) {
        candidates.push({
          amount,
          source: "OTA rate",
          title: rp?.title || "OTA Rate Plan",
        });
      }
    });
  });
  return minCandidate(candidates);
}

function readAvailability(room, date, initialPreview) {
  if (
    initialPreview?.availability !== undefined &&
    initialPreview?.availability !== null
  ) {
    return Number(initialPreview.availability);
  }
  const presence = room?.availability_source?.[date];
  const raw = room?.availability?.[date];
  if (presence === "missing") return null;
  if (presence === "stored" || presence === "channel_manager") return Number(raw);
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function draftFromBeRows(rows) {
  const result = {};
  (rows || []).forEach((row) => {
    result[String(row.id)] = {
      rate_id: Number(row.rateId),
      occ1_rate: row.occ1,
      occ2_rate: row.occ2,
      guest_wise_rates: { ...(row.guestWise || {}) },
      currency_code: row.currencyCode,
    };
  });
  return result;
}

export default function DistributionDayPreviewDrawer({
  open,
  onClose,
  room,
  date,
  beRateOptions = [],
  beReadState = null,
  currency = "INR",
  initialPreview,
  savingAvailability = false,
  savingDirectRate = false,
  savingOtaRate = false,
  distributionStatus = null,
  onSaveSellingStatus,
  onSaveDirectPricing,
  onSaveOtaPricing,
  onSaveCommercialPricing,
}) {
  const persistedRule = room?.restrictions?.[date] || null;
  const [availability, setAvailability] = useState(null);
  const [beRates, setBeRates] = useState([]);
  const [otaRates, setOtaRates] = useState([]);
  const [otaDrafts, setOtaDrafts] = useState({});
  const [otaDirty, setOtaDirty] = useState({});
  const [directBaseRate, setDirectBaseRate] = useState(null);
  const [directBaseDirty, setDirectBaseDirty] = useState(false);
  const [directDrafts, setDirectDrafts] = useState({});
  const [directDirty, setDirectDirty] = useState({});
  const [minStay, setMinStay] = useState(1);
  const [stopSell, setStopSell] = useState(false);
  const [cta, setCta] = useState(false);
  const [ctd, setCtd] = useState(false);
  const [ruleDirty, setRuleDirty] = useState({});

  useEffect(() => {
    if (!open || !room || !date) return;

    const rows = initialPreview?.beRates || beRows(beRateOptions, date, beReadState);
    setAvailability(readAvailability(room, date, initialPreview));
    setBeRates(rows);
    setDirectDrafts(draftFromBeRows(rows));
    setDirectDirty({});

    const otaRows = initialPreview?.otaRates || otaRateRows(room, date);
    setOtaRates(otaRows);
    setOtaDrafts(
      Object.fromEntries(
        otaRows.map((rate) => [
          String(rate.id),
          {
            rate_plan_id: String(rate.id),
            rates: { ...(rate.rates || {}) },
          },
        ])
      )
    );
    setOtaDirty({});

    const row = beReadState?.rowsByDate?.[date] || null;
    const defaults = beReadState?.roomTypeDefaults || {};
    const inheritedRoomTypeRate = [
      defaults?.base_rate_single,
      defaults?.base_rate_double,
    ]
      .map(Number)
      .find((value) => Number.isFinite(value) && value > 0);

    setDirectBaseRate(
      row?.override_rate !== null &&
        row?.override_rate !== undefined &&
        row?.override_rate !== ""
        ? Number(row.override_rate)
        : inheritedRoomTypeRate ?? null
    );
    setDirectBaseDirty(false);

    const persisted = room?.restrictions?.[date] || null;
    const beRow = beReadState?.rowsByDate?.[date] || {};

    setMinStay(
      Math.max(
        1,
        Number(
          persisted?.min_stay ??
            persisted?.min_stay_arrival ??
            beRow?.min_stay ??
            beRow?.minStay ??
            1
        ) || 1
      )
    );
    setStopSell(
      Boolean(
        persisted?.stop_sell ??
          beRow?.stop_sell ??
          beRow?.stopSell ??
          false
      )
    );
    setCta(
      Boolean(
        persisted?.closed_to_arrival ??
          beRow?.closed_to_arrival ??
          beRow?.cta ??
          false
      )
    );
    setCtd(
      Boolean(
        persisted?.closed_to_departure ??
          beRow?.closed_to_departure ??
          beRow?.ctd ??
          false
      )
    );
    setRuleDirty({});
  }, [open, room, date, beRateOptions, beReadState, initialPreview]);

  const directSummary = useMemo(
    () => getDirectSummary(beRateOptions, date, beReadState),
    [beRateOptions, date, beReadState]
  );
  const otaSummary = useMemo(
    () => getOtaSummary(room, date),
    [room, date]
  );

  const commercialDifference = useMemo(() => {
    const direct = Number(directSummary?.amount);
    const ota = Number(otaSummary?.amount);
    if (!Number.isFinite(direct) || !Number.isFinite(ota)) return null;
    const amount = ota - direct;
    const pct = direct > 0 ? (amount / direct) * 100 : null;
    return { amount, pct };
  }, [directSummary, otaSummary]);

  const saveSellingStatus = async () => {
    const restrictions = {};
    if (ruleDirty.minStay) restrictions.minStay = Math.max(1, Number(minStay || 1));
    if (ruleDirty.stopSell) restrictions.stopSell = Boolean(stopSell);
    if (ruleDirty.cta) restrictions.cta = Boolean(cta);
    if (ruleDirty.ctd) restrictions.ctd = Boolean(ctd);

    const result = await onSaveSellingStatus?.({
      roomId: room?.pms_room_type_id,
      date,
      availability:
        availability === null || availability === undefined
          ? null
          : Number(availability),
      restrictions,
    });

    if (result?.success) onClose?.();
  };

  const markDirectField = (rateId, field, value) => {
    const key = String(rateId);
    setDirectDrafts((current) => ({
      ...current,
      [key]: {
        ...(current[key] || { rate_id: Number(rateId), guest_wise_rates: {} }),
        [field]: value,
      },
    }));
    setDirectDirty((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        [field]: true,
      },
    }));
  };

  const markDirectGuest = (rateId, guestCount, value) => {
    const key = String(rateId);
    setDirectDrafts((current) => ({
      ...current,
      [key]: {
        ...(current[key] || { rate_id: Number(rateId), guest_wise_rates: {} }),
        guest_wise_rates: {
          ...(current[key]?.guest_wise_rates || {}),
          [String(guestCount)]: value,
        },
      },
    }));
    setDirectDirty((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        guest_wise_rates: {
          ...(current[key]?.guest_wise_rates || {}),
          [String(guestCount)]: true,
        },
      },
    }));
  };


  const markOtaRate = (ratePlanId, occupancy, value) => {
    const planKey = String(ratePlanId);
    const occKey = String(Number(occupancy));
    setOtaDrafts((current) => ({
      ...current,
      [planKey]: {
        ...(current[planKey] || { rate_plan_id: planKey, rates: {} }),
        rates: {
          ...(current[planKey]?.rates || {}),
          [occKey]: value,
        },
      },
    }));
    setOtaDirty((current) => ({
      ...current,
      [planKey]: {
        ...(current[planKey] || {}),
        [occKey]: true,
      },
    }));
  };

  const buildDirectPricingPayload = () => {
    if (!hasBePlans) {
      if (!directBaseDirty) return null;
      const numeric = Number(directBaseRate);
      if (!Number.isFinite(numeric) || numeric < 0) return null;
      return { baseRate: numeric, rateOverrides: [] };
    }

    const rateOverrides = [];

    Object.entries(directDirty || {}).forEach(([rateId, dirty]) => {
      const draft = directDrafts?.[rateId] || {};
      const patch = {
        rate_id: Number(rateId),
        currency_code: String(
          draft?.currency_code || currency || "INR"
        ).toUpperCase(),
      };

      if (dirty?.occ1_rate) {
        const value = Number(draft.occ1_rate);
        if (Number.isFinite(value) && value >= 0) patch.occ1_rate = value;
      }
      if (dirty?.occ2_rate) {
        const value = Number(draft.occ2_rate);
        if (Number.isFinite(value) && value >= 0) patch.occ2_rate = value;
      }

      const guestPatch = {};
      Object.entries(dirty?.guest_wise_rates || {}).forEach(
        ([guestCount, isDirty]) => {
          if (!isDirty) return;
          const value = draft?.guest_wise_rates?.[guestCount];
          if (value === null || value === undefined || value === "") return;
          const numeric = Number(value);
          if (!Number.isFinite(numeric) || numeric < 0) return;
          guestPatch[String(guestCount)] = numeric;
        }
      );
      if (Object.keys(guestPatch).length) patch.guest_wise_rates = guestPatch;

      if (
        Object.prototype.hasOwnProperty.call(patch, "occ1_rate") ||
        Object.prototype.hasOwnProperty.call(patch, "occ2_rate") ||
        Object.keys(patch.guest_wise_rates || {}).length
      ) {
        rateOverrides.push(patch);
      }
    });

    return rateOverrides.length ? { rateOverrides } : null;
  };

  const buildOtaPricingPayload = () => {
    const rates = [];
    Object.entries(otaDirty || {}).forEach(([ratePlanId, dirtyByOcc]) => {
      Object.entries(dirtyByOcc || {}).forEach(([occupancy, isDirty]) => {
        if (!isDirty) return;
        const value = otaDrafts?.[ratePlanId]?.rates?.[occupancy];
        if (value === null || value === undefined || value === "") return;
        const amount = Number(value);
        if (!Number.isFinite(amount) || amount < 0) return;
        rates.push({
          rate_plan_id: String(ratePlanId),
          occupancy: Math.max(1, Number(occupancy) || 1),
          amount,
        });
      });
    });
    return rates;
  };

  const saveDirectPlanRates = async () => {
    const directPricing = buildDirectPricingPayload();
    if (!directPricing?.rateOverrides?.length) return;

    const result = await onSaveDirectPricing?.({
      roomId: room?.pms_room_type_id,
      date,
      rateOverrides: directPricing.rateOverrides,
    });

    if (result?.success) onClose?.();
  };

  const saveDirectBaseRate = async () => {
    const directPricing = buildDirectPricingPayload();
    if (
      !directPricing ||
      directPricing.baseRate === null ||
      directPricing.baseRate === undefined
    ) {
      return;
    }

    const result = await onSaveDirectPricing?.({
      roomId: room?.pms_room_type_id,
      date,
      baseRate: Number(directPricing.baseRate),
    });

    if (result?.success) onClose?.();
  };

  const saveOtaPricing = async () => {
    const rates = buildOtaPricingPayload();
    if (!rates.length) return;

    const result = await onSaveOtaPricing?.({
      roomId: room?.pms_room_type_id,
      date,
      rates,
    });

    if (result?.success) onClose?.();
  };

  const saveAllPricing = async () => {
    const directPricing = buildDirectPricingPayload();
    const otaRates = buildOtaPricingPayload();

    const result = await onSaveCommercialPricing?.({
      roomId: room?.pms_room_type_id,
      date,
      directPricing,
      otaRates,
    });

    if (result?.success) onClose?.();
  };

  const maxStay = persistedRule?.max_stay ?? null;
  const hasGlobalRule = Boolean(persistedRule);
  const hasBePlans = beRates.length > 0;
  const directWritable = beReadState?.status === "live";
  const hasDirectDirty =
    directBaseDirty ||
    Object.keys(directDirty || {}).some((rateId) => {
      const dirty = directDirty[rateId] || {};
      return (
        dirty.occ1_rate ||
        dirty.occ2_rate ||
        Object.values(dirty.guest_wise_rates || {}).some(Boolean)
      );
    });
  const hasOtaDirty = Object.values(otaDirty || {}).some((byOcc) =>
    Object.values(byOcc || {}).some(Boolean)
  );
  const hasAnyPricingDirty = hasDirectDirty || hasOtaDirty;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      zIndex={6500}
      width={760}
      closable={false}
      rootClassName="cm-distribution-drawer cm-distribution-day-drawer"
      keyboard
      destroyOnClose={false}
      styles={{
        body: { padding: 0, background: "#f8fafc" },
        content: { borderRadius: "18px 0 0 18px", overflow: "hidden" },
      }}
    >
      <div className="dist-drawer-header dist-drawer-header-compact">
        <div>
          <div className="dist-preview-kicker">Daily Control</div>
          <h3>{room?.name || "Room Type"}</h3>
          <div className="dist-drawer-subtitle">
            {date ? dayjs(date).format("dddd, DD MMMM YYYY") : ""}
          </div>
        </div>
        <div className="dist-drawer-head-actions">
          <button type="button" className="dist-close-btn" onClick={onClose} aria-label="Close daily control">
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="dist-drawer-body">
        <section className="dist-section-card dist-selling-card">
          <div className="dist-section-heading">
            <div className="dist-section-icon"><Globe size={18} /></div>
            <div>
              <div className="dist-domain-eyebrow direct">GLOBAL SELLING STATUS</div>
              <h4>Inventory & Restrictions</h4>
              <p>
                Availability, Stop Sell, Minimum Stay, CTA and CTD remain one
                operational rule set for Direct + all OTAs.
              </p>
            </div>
            <Tag color="green">LIVE GLOBAL WRITE</Tag>
          </div>

          <div className="dist-rule-source-note">
            <strong>
              {hasGlobalRule
                ? "Global rule loaded"
                : "Global rule not initialized yet"}
            </strong>
            <span>
              {hasGlobalRule
                ? `NetShine desired state · ${persistedRule?.sync_status || "saved_local"}`
                : "Booking Engine values are shown as a safe starting point. Only changed fields are written."}
            </span>
          </div>

          <div className="dist-availability-editor">
            <div>
              <span className="dist-field-label">Available Rooms</span>
              <div className="dist-availability-input-line">
                <InputNumber
                  min={0}
                  value={availability}
                  placeholder="Not set"
                  onChange={(v) =>
                    setAvailability(v === null ? null : Number(v))
                  }
                />
                <span>
                  of <strong>{Number(room?.total_rooms || 0)}</strong> physical
                </span>
              </div>
              {availability === null ? (
                <small className="dist-field-help">
                  No explicit Channel Manager ARI value exists for this date.
                </small>
              ) : null}
            </div>

            <label>
              <span className="dist-field-label">Minimum Stay</span>
              <InputNumber
                min={1}
                max={30}
                value={minStay}
                addonAfter="nights"
                onChange={(v) => {
                  setMinStay(Math.max(1, Number(v || 1)));
                  setRuleDirty((current) => ({
                    ...current,
                    minStay: true,
                  }));
                }}
              />
            </label>

            <label>
              <span className="dist-field-label">Maximum Stay</span>
              <InputNumber
                disabled
                min={0}
                value={maxStay}
                placeholder="Not set"
                addonAfter="nights"
              />
              <small className="dist-field-help neutral">
                Not global yet because Booking Engine Manual Inventory has no
                Max Stay field.
              </small>
            </label>
          </div>

          <div className="dist-switch-list dist-switch-list-compact">
            <div>
              <div>
                <strong>Stop Sell</strong>
                <span>Close or reopen this date everywhere.</span>
              </div>
              <Switch
                checked={stopSell}
                onChange={(v) => {
                  setStopSell(v);
                  setRuleDirty((current) => ({
                    ...current,
                    stopSell: true,
                  }));
                }}
              />
            </div>
            <div>
              <div>
                <strong>Closed to Arrival</strong>
                <span>Prevent new stays from starting on this date.</span>
              </div>
              <Switch
                checked={cta}
                onChange={(v) => {
                  setCta(v);
                  setRuleDirty((current) => ({ ...current, cta: true }));
                }}
              />
            </div>
            <div>
              <div>
                <strong>Closed to Departure</strong>
                <span>Prevent stays from ending on this date.</span>
              </div>
              <Switch
                checked={ctd}
                onChange={(v) => {
                  setCtd(v);
                  setRuleDirty((current) => ({ ...current, ctd: true }));
                }}
              />
            </div>
          </div>

          <div className="dist-applies-everywhere">
            <strong>Global target</strong>
            <span>Booking Engine</span>
            <span>All OTAs via existing Channex ARI</span>
          </div>

          <div className="dist-inline-action">
            <Button
              type="primary"
              loading={savingAvailability}
              disabled={
                availability === null ||
                !Number.isFinite(Number(availability))
              }
              onClick={saveSellingStatus}
            >
              Save Global Selling Status
            </Button>
          </div>
        </section>

        <div className="dist-pricing-divider">
          <span>Commercial Pricing</span>
          <small>
            Edit Direct and OTA independently, then save either section or both together.
          </small>
        </div>

        <div className="dist-pricing-workflow-note">
          <div>
            <strong>One-screen pricing workflow</strong>
            <span>
              Direct and OTA keep separate rate engines. You can still use one
              <b> Save Pricing</b> action when both need changing.
            </span>
          </div>
          <Tag color="blue">COMBINED SAVE AVAILABLE</Tag>
        </div>

        <section className="dist-rate-overview-card">
          <div className="dist-rate-overview-title">
            <div>
              <span className="dist-preview-kicker">Rate overview</span>
              <strong>What the guest sees by channel</strong>
            </div>
            <TrendingUp size={18} />
          </div>
          <div className="dist-rate-overview-grid">
            <div className="direct">
              <span>Direct</span>
              <strong>
                {directSummary ? money(directSummary.amount, currency) : "—"}
              </strong>
              <small>
                {directSummary
                  ? `${directSummary.source} · ${directSummary.title}`
                  : "Not priced"}
              </small>
            </div>
            <div className="ota">
              <span>OTA</span>
              <strong>{otaSummary ? money(otaSummary.amount, currency) : "—"}</strong>
              <small>{otaSummary ? otaSummary.title : "Not priced"}</small>
            </div>
            <div className="difference">
              <span>OTA vs Direct</span>
              <strong>
                {commercialDifference
                  ? `${commercialDifference.amount >= 0 ? "+" : "−"}${money(
                      Math.abs(commercialDifference.amount),
                      currency
                    )}`
                  : "—"}
              </strong>
              <small>
                {commercialDifference?.pct !== null &&
                commercialDifference?.pct !== undefined
                  ? `${commercialDifference.pct >= 0 ? "+" : ""}${commercialDifference.pct.toFixed(1)}%`
                  : "Needs both rates"}
              </small>
            </div>
          </div>
        </section>

        <section className="dist-section-card dist-pricing-domain-card direct-domain">
          <div className="dist-section-heading">
            <div className="dist-section-icon direct"><Bed size={18} /></div>
            <div>
              <div className="dist-domain-eyebrow direct">DIRECT / BOOKING ENGINE</div>
              <h4>Direct Date Pricing</h4>
              <p>
                Edit date-specific prices here. Master Booking Engine rate plans
                stay intact and are used as fallback.
              </p>
            </div>
            {directWritable ? (
              <Tag className="dist-direct-tag">LIVE DATE WRITE</Tag>
            ) : (
              <Tag className="dist-direct-tag"><Lock size={12} /> Unavailable</Tag>
            )}
          </div>

          {beReadState?.status === "unmapped" ? (
            <div className="dist-empty-rate-state">
              No exact Booking Engine room-type ID match exists for this
              Channel Manager room.
            </div>
          ) : beReadState?.status === "error" ? (
            <div className="dist-empty-rate-state">
              {beReadState?.message ||
                "Booking Engine Direct pricing could not be loaded."}
            </div>
          ) : beReadState?.status === "loading" ? (
            <div className="dist-empty-rate-state">
              Loading Booking Engine rates and date overrides…
            </div>
          ) : !hasBePlans ? (
            <div className="dist-base-rate-editor">
              <div>
                <strong>Room Type price</strong>
                <span>
                  No Booking Engine rate plan is configured, so the Room Type
                  master price is the Direct default. Editing below creates the
                  existing date <code>override_rate</code>; it does not create a fake plan.
                </span>
              </div>

              <div className="dist-roomtype-default-rates">
                <span>
                  Single
                  <b>{money(beReadState?.roomTypeDefaults?.base_rate_single, currency)}</b>
                </span>
                <span>
                  Double
                  <b>{money(beReadState?.roomTypeDefaults?.base_rate_double, currency)}</b>
                </span>
              </div>

              <div className="dist-base-rate-actions">
                <InputNumber
                  min={0}
                  value={directBaseRate}
                  prefix={currencySymbol(currency)}
                  placeholder="Room Type / date price"
                  onChange={(v) => {
                    setDirectBaseRate(v === null ? null : Number(v));
                    setDirectBaseDirty(true);
                  }}
                />
                <Button
                  type="primary"
                  loading={savingDirectRate}
                  disabled={
                    !directWritable ||
                    !directBaseDirty ||
                    directBaseRate === null ||
                    !Number.isFinite(Number(directBaseRate))
                  }
                  onClick={saveDirectBaseRate}
                >
                  Save Direct Rate
                </Button>
              </div>
              <small>
                {directSummary
                  ? `Effective Direct rate: ${money(
                      directSummary.amount,
                      currency
                    )} · ${directSummary.source}`
                  : "Room Type master pricing is not configured yet."}
              </small>
            </div>
          ) : (
            <>
              <div className="dist-direct-edit-note">
                <strong>Date overrides only</strong>
                <span>
                  Editing a value below creates/updates the selected date
                  override. Unchanged rates and guest counts are preserved. This release updates overrides; it does not delete an existing override by clearing an input.
                </span>
              </div>

              <div className="dist-rate-plan-stack">
                {beRates.map((rate) => {
                  const draft = directDrafts?.[rate.id] || {};
                  const dirty = directDirty?.[rate.id] || {};

                  return (
                    <div className="dist-rate-plan dist-rate-plan-editable" key={rate.id}>
                      <div className="dist-rate-plan-title">
                        <div>
                          <strong>{rate.title}</strong>
                          <small>
                            {rate.source === "date_override"
                              ? "Date override active"
                              : "Using master rate"}
                          </small>
                        </div>
                        <span>
                          {rate.source === "date_override"
                            ? "Date override"
                            : "Master fallback"}
                        </span>
                      </div>

                      {rate.isGuestWise ? (
                        <div className="dist-direct-guest-grid">
                          {Array.from(
                            new Set([
                              ...Object.keys(rate.masterGuestWise || {}),
                              ...Object.keys(rate.guestWise || {}),
                            ])
                          )
                            .sort((a, b) => Number(a) - Number(b))
                            .map((guest) => (
                              <label
                                key={guest}
                                className={
                                  dirty?.guest_wise_rates?.[guest]
                                    ? "is-edited"
                                    : ""
                                }
                              >
                                <span>
                                  {guest} guest{String(guest) === "1" ? "" : "s"}
                                </span>
                                <InputNumber
                                  min={0}
                                  prefix={currencySymbol(currency)}
                                  value={draft?.guest_wise_rates?.[guest]}
                                  onChange={(v) =>
                                    markDirectGuest(
                                      rate.rateId,
                                      guest,
                                      v === null ? null : Number(v)
                                    )
                                  }
                                />
                                <small>
                                  Master{" "}
                                  {money(rate.masterGuestWise?.[guest], currency)}
                                </small>
                              </label>
                            ))}
                        </div>
                      ) : (
                        <div className="dist-rate-grid">
                          <label className={dirty?.occ1_rate ? "is-edited" : ""}>
                            <span>Single</span>
                            <InputNumber
                              min={0}
                              prefix={currencySymbol(currency)}
                              value={draft?.occ1_rate}
                              onChange={(v) =>
                                markDirectField(
                                  rate.rateId,
                                  "occ1_rate",
                                  v === null ? null : Number(v)
                                )
                              }
                            />
                            <small>Master {money(rate.masterOcc1, currency)}</small>
                          </label>
                          <label className={dirty?.occ2_rate ? "is-edited" : ""}>
                            <span>Double</span>
                            <InputNumber
                              min={0}
                              prefix={currencySymbol(currency)}
                              value={draft?.occ2_rate}
                              onChange={(v) =>
                                markDirectField(
                                  rate.rateId,
                                  "occ2_rate",
                                  v === null ? null : Number(v)
                                )
                              }
                            />
                            <small>Master {money(rate.masterOcc2, currency)}</small>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="dist-inline-action dist-inline-action-between">
                <Text type="secondary">
                  {hasDirectDirty
                    ? "Only edited Direct fields will be merged into this date."
                    : "Change one or more Direct values to create a date override."}
                </Text>
                <Button
                  type="primary"
                  loading={savingDirectRate}
                  disabled={!directWritable || !hasDirectDirty}
                  onClick={saveDirectPlanRates}
                >
                  Save Direct Pricing
                </Button>
              </div>
            </>
          )}
        </section>

        <section className="dist-section-card dist-pricing-domain-card ota-domain">
          <div className="dist-section-heading">
            <div className="dist-section-icon ota"><DollarSign size={18} /></div>
            <div>
              <div className="dist-domain-eyebrow ota">OTA / CHANNEL MANAGER</div>
              <h4>OTA Date Pricing</h4>
              <p>
                Edit the existing mapped Channel Manager / Channex rate plans.
                Only changed plan + occupancy values are written.
              </p>
            </div>
            <Tag className="dist-ota-tag">LIVE OTA WRITE</Tag>
          </div>

          {!otaRates.length ? (
            <div className="dist-empty-rate-state">
              No mapped OTA rate plans were returned for this room/date.
            </div>
          ) : (
            <>
              <div className="dist-direct-edit-note ota-note">
                <strong>Mapped plans stay independent</strong>
                <span>
                  NetShine writes OTA prices through the existing
                  <code> cm_ari_rates → Channex /restrictions</code> path.
                  Direct Booking Engine prices are not altered unless you edit them too.
                </span>
              </div>

              <div className="dist-rate-plan-stack">
                {otaRates.map((rate) => {
                  const planDraft = otaDrafts?.[String(rate.id)] || {};
                  const dirtyByOcc = otaDirty?.[String(rate.id)] || {};

                  return (
                    <div className="dist-rate-plan dist-rate-plan-editable" key={rate.id}>
                      <div className="dist-rate-plan-title">
                        <div>
                          <strong>{rate.title}</strong>
                          <small>Mapped Channex rate plan</small>
                        </div>
                        <span>OTA</span>
                      </div>
                      <div className="dist-rate-grid">
                        {rate.occupancies.map((occ) => (
                          <label
                            key={occ}
                            className={dirtyByOcc?.[String(occ)] ? "is-edited" : ""}
                          >
                            <span>×{occ} occupancy</span>
                            <InputNumber
                              min={0}
                              prefix={currencySymbol(currency)}
                              value={
                                planDraft?.rates?.[String(occ)] ??
                                planDraft?.rates?.[occ] ??
                                null
                              }
                              placeholder="Not priced"
                              onChange={(v) =>
                                markOtaRate(
                                  rate.id,
                                  occ,
                                  v === null ? null : Number(v)
                                )
                              }
                            />
                            <small>
                              {dirtyByOcc?.[String(occ)]
                                ? "Will be pushed to Channex"
                                : rate.rates?.[occ] == null
                                  ? "No stored OTA rate"
                                  : `Current ${money(rate.rates?.[occ], currency)}`}
                            </small>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="dist-inline-action dist-inline-action-between">
                <Text type="secondary">
                  {hasOtaDirty
                    ? "Only edited OTA fields will be written and pushed."
                    : "Change one or more OTA values to update mapped channels."}
                </Text>
                <Button
                  type="primary"
                  loading={savingOtaRate}
                  disabled={!hasOtaDirty}
                  onClick={saveOtaPricing}
                >
                  Save OTA Pricing
                </Button>
              </div>
            </>
          )}
        </section>

        <div className="dist-flow-summary dist-flow-summary-compact">
          <CheckCircle size={17} />
          <div>
            <strong>One-screen commercial control</strong>
            <span>
              Global inventory/rules → Booking Engine + Channex. Direct pricing
              → Booking Engine. OTA pricing → Channel Manager + Channex. Edit
              either domain separately or save both pricing domains together.
            </span>
          </div>
        </div>
      </div>

      <div className="dist-drawer-footer dist-pricing-footer">
        <Text type="secondary">
          Separate rate engines, one workspace. Use section-level saves when
          you want independent control, or Save Pricing to commit all edited
          Direct + OTA values in one user action.
        </Text>
        <div>
          <Button
            onClick={onClose}
            disabled={savingAvailability || savingDirectRate || savingOtaRate}
          >
            Close
          </Button>
          <Button
            type="primary"
            loading={savingDirectRate || savingOtaRate}
            disabled={!hasAnyPricingDirty}
            onClick={saveAllPricing}
          >
            Save Pricing
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
